import React, { useState, ErrorInfo, useEffect, useMemo, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { Brain, Code, FileText, Database, Settings, User, MessageCircle, Smile, UserCircle, Sparkles, Plus, Workflow } from 'lucide-react';
import { Link } from 'react-router-dom';
import ScriptEditor from '../../components/NucleoNeural/ScriptEditor';
import ScriptGenerator from '../../components/NucleoNeural/ScriptGenerator';
import { PersonaConfigurator } from '../../components/NucleoNeural/PersonaConfigurator';
import { ProdutoConfigurator } from '../../components/NucleoNeural/ProdutoConfigurator';
import { EmojisConfigurator } from '../../components/NucleoNeural/EmojisConfigurator';
import { IdentidadePessoal } from '../../components/NucleoNeural/IdentidadePessoal';
import { BotFlowBuilder } from '../../components/BotFlowBuilder';
import ConfirmationModal from '../../components/Layout/ConfirmationModal';
import { useScripts, useUpdateScript, useUpdateFatosProduto, useReloadScripts, useResetScripts, useApplyGeneratedScripts } from '../../hooks/useApi';
import type { GeneratedScript } from '../../types/aiGeneration';
import { useCurrentUserId } from '../../hooks/useCurrentUserId';
import { showSuccessToast, showInfoToast, showErrorToast } from '../../utils/toastHelpers';
import SkeletonLoader from '../../components/SkeletonLoader';
import { useSkeletonDelay } from '../../hooks/useSkeletonDelay';

// Error Boundary específico para NucleoNeural
class NucleoNeuralErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('NucleoNeural Error Boundary caught an error:', error);
    console.error('Error info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '50vh',
          gap: '16px',
          color: '#666'
        }}>
          <Brain size={48} style={{ opacity: 0.5 }} />
          <h3>Erro no Núcleo Neural</h3>
          <p>Ocorreu um erro ao carregar o Núcleo Neural. Tente recarregar a página.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              background: '#ff4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Recarregar Página
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Animação elegante para bot desativado
const BotOffContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  color: #ff3b3b;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const BotOffIcon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff3b3b 60%, ${({ theme }) => theme.colors.backgroundSecondary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 40px 0 #ff3b3b44;
`;

const BotOffText = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  color: #ff3b3b;
  margin: 0;
  text-align: center;
`;

const VaultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
  padding: ${({ theme }) => theme.spacing[4]};
  max-width: 1600px;
  margin: 0 auto;
`;

const VaultHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const VaultTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const VaultInfo = styled.div`
  flex: 1;
`;

const VaultName = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const VaultDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const VaultActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

// Tabs Navigation
const TabsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing[6]} 0;
  padding: 0 ${({ theme }) => theme.spacing[2]};
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 3px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: -2px;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  max-width: 1600px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[4]};
`;

const ConfigRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing[6]};
  align-items: stretch;

  > * {
    min-height: 100%;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  border: 1px solid transparent;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: ${props.theme.colors.primary};
          color: white;
          &:hover {
            background: ${props.theme.colors.primaryHover};
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadows.neon};
            color: white;
          }
        `;
      default:
        return `
          background: ${props.theme.colors.backgroundTertiary};
          color: ${props.theme.colors.textSecondary};
          border-color: ${props.theme.colors.border};
          &:hover {
            background: ${props.theme.colors.backgroundSecondary};
            color: ${props.theme.colors.textPrimary};
            border-color: ${props.theme.colors.primary};
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;





const VaultContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0;
  min-height: 80vh;
  height: calc(100vh - 200px);
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    min-height: auto;
  }
`;

// GitHub-inspired horizontal layout
const ScriptSidebar = styled.div`
  width: 320px;
  min-width: 320px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    border-radius: ${({ theme }) => theme.borders.radius.lg};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
`;

const SidebarHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.backgroundTertiary} 0%, ${({ theme }) => theme.colors.backgroundSecondary} 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const SidebarTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ScriptList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[2]};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    border-radius: ${({ theme }) => theme.borders.radius.full};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.full};

    &:hover {
      background: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const ScriptGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const GroupTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const ScriptItem = styled.button<{ $active: boolean; $scriptType: string }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  border: none;
  background: ${props => props.$active ? props.theme.colors.primary + '15' : 'transparent'};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  text-align: left;
  border-left: 3px solid ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  will-change: transform, background-color;
  backface-visibility: hidden;
  transform: translateZ(0);

  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary + '20' : props.theme.colors.backgroundTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
    transform: translateX(2px) translateZ(0);
  }

  &:active {
    transform: translateX(1px) translateZ(0);
  }
`;

const ScriptIcon = styled.div<{ $scriptType: string }>`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.$scriptType) {
      case 'conteudo': return 'linear-gradient(135deg, #6b7280, #4b5563)';
      case 'mentalidade': return 'linear-gradient(135deg, #ef4444, #dc2626)';
      case 'fatos_produto': return 'linear-gradient(135deg, #f59e0b, #d97706)';
      case 'persona': return 'linear-gradient(135deg, #8b5cf6, #7c3aed)';
      case 'girias': return 'linear-gradient(135deg, #10b981, #059669)';
      case 'abreviacoes': return 'linear-gradient(135deg, #14b8a6, #0d9488)';
      case 'emojis': return 'linear-gradient(135deg, #f472b6, #ec4899)';
      default: return props.theme.colors.backgroundTertiary;
    }
  }};
  color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  will-change: transform;
  backface-visibility: hidden;
  transform: translateZ(0);

  ${props => {
    const animations = {
      conteudo: 'fadeIn 0.6s ease-in-out',
      mentalidade: 'pulse 2s ease-in-out infinite',
      fatos_produto: 'scale 0.8s ease-in-out',
      persona: 'pulse 2s ease-in-out infinite',
      girias: 'fadeIn 0.6s ease-in-out',
      abreviacoes: 'fadeIn 0.6s ease-in-out',
      emojis: 'pulse 2s ease-in-out infinite'
    };
    return `animation: ${animations[props.$scriptType as keyof typeof animations] || 'none'};`;
  }}

  @keyframes pulse {
    0%, 100% { transform: scale(1) translateZ(0); }
    50% { transform: scale(1.05) translateZ(0); }
  }

  @keyframes rotate {
    from { transform: rotate(0deg) translateZ(0); }
    to { transform: rotate(360deg) translateZ(0); }
  }

  @keyframes scale {
    0%, 100% { transform: scale(1) translateZ(0); }
    50% { transform: scale(1.1) translateZ(0); }
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-2px) translateZ(0); }
    to { opacity: 1; transform: translateY(0) translateZ(0); }
  }
`;

const ScriptInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScriptName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScriptType = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: capitalize;
`;

const ScriptDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  margin-top: ${({ theme }) => theme.spacing[1]};
  opacity: 0.8;
`;

const EditorWorkspace = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  will-change: transform;
  transform: translateZ(0);

  @media (max-width: 768px) {
    border-left: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.lg};
  }
`;

// Seção separada para o Chat de IA
const AIChatSection = styled.div`
  width: 100%;
  height: 650px;
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  overflow: hidden;
  border: 2px solid ${({ theme }) => theme.colors.primary + '20'};
  box-shadow: ${({ theme }) => theme.shadows.neon};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.primary}40,
      transparent 30%,
      transparent 70%,
      ${({ theme }) => theme.colors.primary}40
    );
    border-radius: ${({ theme }) => theme.borders.radius.xl};
    z-index: -1;
    animation: shimmerBorder 3s ease-in-out infinite;
  }

  @keyframes shimmerBorder {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }

  @media (max-width: 768px) {
    height: 550px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  gap: ${({ theme }) => theme.spacing[4]};
  animation: fadeInUp 0.6s ease-out;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.backgroundTertiary} 0%, ${({ theme }) => theme.colors.border} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  will-change: transform;
  transform: translateZ(0);
  animation: gentlePulse 3s ease-in-out infinite;

  @keyframes gentlePulse {
    0%, 100% {
      transform: scale(1) translateZ(0);
      opacity: 0.6;
    }
    50% {
      transform: scale(1.05) translateZ(0);
      opacity: 0.8;
    }
  }
`;

const MobileToggle = styled.button`
  display: none;
  will-change: transform, opacity;
  transform: translateZ(0);

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.md};
    color: ${({ theme }) => theme.colors.textSecondary};
    cursor: pointer;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

    &:hover {
      background: ${({ theme }) => theme.colors.backgroundSecondary};
      color: ${({ theme }) => theme.colors.textPrimary};
      border-color: ${({ theme }) => theme.colors.primary};
      transform: translateY(-1px) translateZ(0);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0) translateZ(0);
    }
  }
`;



const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 75vh;
  color: ${({ theme }) => theme.colors.textSecondary};
  gap: ${({ theme }) => theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${({ theme }) => theme.colors.border};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  will-change: transform;
  transform: translateZ(0);

  @keyframes spin {
    from { transform: rotate(0deg) translateZ(0); }
    to { transform: rotate(360deg) translateZ(0); }
  }
`;

const NucleoNeural: React.FC = () => {
  const theme = useTheme();

  // Obter ID do usuário atual
  const { userId, loading: userLoading, isLoggedIn } = useCurrentUserId();

  // Status do bot simplificado
  const botLoading = false;
  const botActive = true;
  const botError = false;

  // Carregar scripts reais do backend com ID do usuário
  const { data: scriptsData, isLoading: scriptsLoading, error: scriptsError } = useScripts(userId || undefined);

  // Hooks para mutations
  const updateScriptMutation = useUpdateScript();
  const updateFatosProdutoMutation = useUpdateFatosProduto();
  const reloadScriptsMutation = useReloadScripts();
  const resetScriptsMutation = useResetScripts();
  const applyGeneratedScriptsMutation = useApplyGeneratedScripts();

  const [selectedScript, setSelectedScript] = useState<{
    type: 'conteudo' | 'mentalidade' | 'fatos_produto' | 'persona' | 'girias' | 'abreviacoes' | 'emojis';
    name: string;
    category?: string;
  } | null>(null);

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // ✅ State para controlar qual tab está ativa
  const [activeTab, setActiveTab] = useState<'identidade' | 'construtor' | 'ia_generator'>('identidade');

  // State para modal de confirmação de reset
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);


  // Responsive hook
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Função segura para selecionar script com useCallback para performance
  const handleScriptSelect = useCallback((script: {
    type: 'conteudo' | 'mentalidade' | 'fatos_produto' | 'persona' | 'girias' | 'abreviacoes' | 'emojis';
    name: string;
    category?: string;
  }) => {
    try {
      setSelectedScript(script);
    } catch (error) {
      console.error('Erro ao selecionar script:', error);
    }
  }, []);


  const handleSave = async (content: string) => {
    if (!selectedScript || !userId) {
      console.error('❌ Erro: Script ou usuário não selecionado', { selectedScript, userId });
      throw new Error('Script ou usuário não selecionado');
    }

    try {
      console.log('📝 Salvando script:', {
        type: selectedScript.type,
        name: selectedScript.name,
        userId,
        contentLength: content.length
      });

      if (selectedScript.type === 'fatos_produto') {
        console.log('💾 Salvando fatos_produto via API...');
        await updateFatosProdutoMutation.mutateAsync({
          conteudo: content,
          userId
        });
      } else if (selectedScript.type === 'persona' || selectedScript.type === 'girias' ||
                 selectedScript.type === 'abreviacoes' || selectedScript.type === 'emojis') {
        console.log(`💾 Salvando ${selectedScript.type} via API...`);
        // Novos scripts usam o mesmo endpoint mas com tipo diferente
        await updateScriptMutation.mutateAsync({
          tipo: selectedScript.type,
          nome: selectedScript.name,
          conteudo: content,
          userId
        });
      } else {
        console.log('💾 Salvando script via API...');
        await updateScriptMutation.mutateAsync({
          tipo: selectedScript.type,
          nome: selectedScript.name,
          conteudo: content,
          userId
        });
      }

      // Notificação de sucesso padronizada
      showSuccessToast(`Script "${selectedScript.name}" salvo com sucesso!`, theme);

      console.log('✅ Script personalizado salvo com sucesso para usuário:', userId);
    } catch (error) {
      console.error('❌ Erro ao salvar script:', error);
      throw error;
    }
  };

  const handleTest = async (content: string) => {
    if (!selectedScript) {
      return 'Nenhum script selecionado para teste.';
    }

    try {
      // Simulate testing the script with more realistic feedback
      await new Promise(resolve => setTimeout(resolve, 1000));

      const scriptInfo = `Tipo: ${selectedScript.type}\nNome: ${selectedScript.name}`;
      const contentPreview = content.length > 100 ? content.substring(0, 100) + '...' : content;

      return `📊 Teste do Script\n\n${scriptInfo}\n\n📝 Conteúdo:\n${contentPreview}\n\n✅ Script validado com sucesso!\n\nO script está pronto para uso no sistema.`;
    } catch (error) {
      console.error('Erro ao testar script:', error);
      return `❌ Erro ao testar script: ${error instanceof Error ? error.message : 'Erro desconhecido'}`;
    }
  };

  const getScriptContent = () => {
    if (!selectedScript || !scriptsData?.data) return '';

    switch (selectedScript.type) {
      case 'conteudo':
        return scriptsData.data.conteudo[selectedScript.name] || '';
      case 'mentalidade':
        return scriptsData.data.mentalidades[selectedScript.name] || '';
      case 'fatos_produto':
        return scriptsData.data.fatos_produto || '';
      case 'persona':
        return (scriptsData.data as any).persona || '';
      case 'girias':
        return (scriptsData.data as any).girias || '';
      case 'abreviacoes':
        return (scriptsData.data as any).abreviacoes || '';
      case 'emojis':
        return (scriptsData.data as any).emojis || '';
      default:
        return '';
    }
  };

  // Memoized scripts para evitar recalculações
      const availableScripts = useMemo(() => {
      if (!scriptsData?.data) return [];
  
      const scriptIntentions: Record<string, string> = {
        'preco': 'VENDA',
        'beneficios': 'VENDA',
        'objecoes': 'SUPORTE',
        'storytelling': 'FORA_ESCOPO',
        'saudacao': 'SAUDACAO',
      };
  
      const scripts = [
        ...Object.keys(scriptsData.data.conteudo || {}).map(name => ({
          type: 'conteudo' as const,
          name,
          content: scriptsData.data.conteudo[name] || '',
          category: 'Scripts de Conteúdo',
          description: 'Scripts principais de resposta',
          intention: scriptIntentions[name] || 'VENDA',
        })),
        ...Object.keys(scriptsData.data.mentalidades || {}).map(name => ({
          type: 'mentalidade' as const,
          name,
          content: scriptsData.data.mentalidades[name] || '',
          category: 'Mentalidades',
          description: 'Personalidade e comportamento do bot',
          intention: 'SUPORTE',
        })),
        {
          type: 'fatos_produto' as const,
          name: 'fatos_produto',
          content: scriptsData.data.fatos_produto || '',
          category: 'Informações do Produto',
          description: 'Fatos e informações sobre produtos',
          intention: 'VENDA',
        },
        ...((scriptsData.data as any).persona ? [{
          type: 'persona' as const,
          name: 'persona',
          content: (scriptsData.data as any).persona || '',
          category: 'Configuração de Persona',
          description: 'Identidade e comunicação do bot'
        }] : []),
        ...((scriptsData.data as any).girias ? [{
          type: 'girias' as const,
          name: 'girias',
          content: (scriptsData.data as any).girias || '',
          category: 'Linguagem',
          description: 'Gírias permitidas para uso natural'
        }] : []),
        ...((scriptsData.data as any).abreviacoes ? [{
          type: 'abreviacoes' as const,
          name: 'abreviacoes',
          content: (scriptsData.data as any).abreviacoes || '',
          category: 'Linguagem',
          description: 'Abreviações permitidas'
        }] : []),
        ...((scriptsData.data as any).emojis ? [{
          type: 'emojis' as const,
          name: 'emojis',
          content: (scriptsData.data as any).emojis || '',
          category: 'Emojis',
          description: 'Paleta de emojis contextuais'
        }] : [])
      ];
  
      return scripts;
    }, [scriptsData?.data]);
  const getScriptIcon = useCallback((type: string) => {
    switch (type) {
      case 'conteudo': return FileText;
      case 'mentalidade': return Brain;
      case 'fatos_produto': return Database;
      case 'persona': return User;
      case 'girias': return MessageCircle;
      case 'abreviacoes': return MessageCircle;
      case 'emojis': return Smile;
      default: return Code;
    }
  }, []);

  const groupedScripts = useMemo(() => {
    const grouped = availableScripts.reduce((acc, script) => {
      const category = script.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(script);
      return acc;
    }, {} as Record<string, typeof availableScripts>);

    return grouped;
  }, [availableScripts]);


  const showSkeleton = useSkeletonDelay(userLoading || botLoading || scriptsLoading, 1000);

  if (showSkeleton) {
    return <SkeletonLoader variant="nucleoNeural" />;
  }

  if (!isLoggedIn) {
    return (
      <LoadingState>
        <div style={{ color: theme.colors.error, textAlign: 'center' }}>
          <h3>Acesso negado</h3>
          <p>Faça login com sua conta Google para acessar o Núcleo Neural.</p>
        </div>
      </LoadingState>
    );
  }

  if (scriptsError) {
    return (
      <LoadingState>
        <div style={{ color: theme.colors.error, textAlign: 'center' }}>
          <h3>Erro ao carregar scripts</h3>
          <p>Não foi possível conectar ao backend. Verifique se o servidor está rodando.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              background: theme.colors.error,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '16px'
            }}
          >
            Tentar Novamente
          </button>
        </div>
      </LoadingState>
    );
  }

  if (botError) {
    return (
      <LoadingState>
        <LoadingSpinner />
        <p>O Bot está desligado, por favor aguarde.</p>
      </LoadingState>
    );
  }

  if (!botActive) {
    return (
      <BotOffContainer>
        <BotOffIcon>
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
            <circle cx="30" cy="30" r="28" stroke="#ff3b3b" strokeWidth="4" fill="#fff0" />
            <line x1="18" y1="18" x2="42" y2="42" stroke="#ff3b3b" strokeWidth="4" strokeLinecap="round" />
            <line x1="42" y1="18" x2="18" y2="42" stroke="#ff3b3b" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </BotOffIcon>
        <BotOffText>Bot desativado</BotOffText>
        <p style={{ color: theme.colors.textSecondary, textAlign: 'center', maxWidth: 400 }}>
          O assistente está <b>desativado</b>.<br />Ative o bot para acessar o Núcleo Neural e recursos de IA.
        </p>
      </BotOffContainer>
    );
  }

  return (
    <VaultContainer>
      <VaultHeader>
        <VaultTitle>
          <VaultInfo>
            <VaultName>Processamento</VaultName>
            <VaultDescription>
              Linguagem Natural
            </VaultDescription>
          </VaultInfo>
        </VaultTitle>
        
        <VaultActions>
          <ActionButton
            $variant="primary"
            onClick={() => setIsResetModalOpen(true)}
            disabled={resetScriptsMutation.isPending || !userId}
          >
            <Brain size={16} />
            {resetScriptsMutation.isPending ? 'Redefinindo...' : 'Redefinir Scripts'}
          </ActionButton>
        </VaultActions>
      </VaultHeader>

      {/* ✅ Tabs: Identidade | Construtor | IA Generator */}
      <TabsContainer>
        <Tab $active={activeTab === 'identidade'} onClick={() => setActiveTab('identidade')}>
          <UserCircle size={18} />
          Identidade Pessoal
        </Tab>
        <Tab $active={activeTab === 'construtor'} onClick={() => setActiveTab('construtor')}>
          <Workflow size={18} />
          Construtor de Bot
        </Tab>
        <Tab $active={activeTab === 'ia_generator'} onClick={() => setActiveTab('ia_generator')}>
          <Sparkles size={18} />
          IA Generator
        </Tab>
      </TabsContainer>

      {/* Conteúdo condicional baseado na tab ativa */}
      {activeTab === 'identidade' ? (
        // ✅ SEÇÃO DE IDENTIDADE PESSOAL
        <IdentidadePessoal />
      ) : activeTab === 'construtor' ? (
        // ✅ SEÇÃO DE CONSTRUTOR DE BOT (Flow Builder)
        <BotFlowBuilder userId={userId} />
      ) : (
        // ✅ SEÇÃO DE IA GENERATOR
        <AIChatSection>
          <ScriptGenerator
            userId={userId || undefined}
            onApproveScript={async (script: GeneratedScript) => {
              if (!userId) return;

              try {
                if (script.type === 'fatos_produto') {
                  await updateFatosProdutoMutation.mutateAsync({
                    conteudo: Array.isArray(script.content) ? script.content.join('\n') : script.content,
                    userId
                  });
                } else {
                  await updateScriptMutation.mutateAsync({
                    tipo: script.type,
                    nome: script.name,
                    conteudo: Array.isArray(script.content) ? script.content.join('\n') : script.content,
                    userId
                  });
                }

                showSuccessToast(`Script "${script.name}" aplicado com sucesso!`, theme);
              } catch (error) {
                console.error('Erro ao aplicar script:', error);
              }
            }}
            onApproveAll={async (scripts: GeneratedScript[]) => {
              if (!userId) return;

              try {
                console.log('🔄 Aplicando scripts gerados...', scripts);
                await applyGeneratedScriptsMutation.mutateAsync({ userId, scripts });
                console.log('✅ Scripts aplicados com sucesso');

                // Aguardar um pouco para garantir que o backend processou e o cache foi limpo
                await new Promise(resolve => setTimeout(resolve, 500));
                console.log('🔄 Cache invalidado, scripts devem estar atualizados');
              } catch (error) {
                console.error('❌ Erro ao aplicar scripts:', error);
                showErrorToast('Erro ao aplicar scripts. Tente novamente.', theme);
              }
            }}
          />
        </AIChatSection>
      )}

      {/* Modal de confirmação para redefinir scripts */}
      <ConfirmationModal
        isOpen={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        onConfirm={async () => {
          if (!userId) return;
          try {
            await resetScriptsMutation.mutateAsync(userId);
            showSuccessToast('Scripts e configurações redefinidas para o padrão com sucesso!', theme);
            setIsResetModalOpen(false);
          } catch (error) {
            console.error('Erro ao redefinir scripts:', error);
            showErrorToast('Erro ao redefinir. Tente novamente.', theme);
          }
        }}
        title="Redefinir Scripts"
        message={
          <>
            <p><strong>Tem certeza que deseja redefinir todos os scripts?</strong></p>
            <p style={{ marginTop: '12px', color: theme.colors.textTertiary }}>
              Esta ação irá restaurar todos os scripts para os valores padrão do sistema.
              Todas as suas personalizações serão perdidas.
            </p>
          </>
        }
        confirmText="Redefinir"
        cancelText="Cancelar"
        loading={resetScriptsMutation.isPending}
      />
    </VaultContainer>
  );
};

export default NucleoNeural;
